// People in this organisation.
// Route: /settings/users
//
// A user row is either NATIVE to this organisation or a MEMBER borrowed from
// another one (`is_member`). For a member only the role is ours to change, and
// "remove" detaches the membership instead of deleting the account. Every place
// that distinction bites, the screen says so in words.

import { useEffect, useMemo, useState } from "react";

import { http, qs } from "../api";
import { useSession } from "../App";
import {
  Badge,
  Confirm,
  Dialog,
  DialogFooter,
  Empty,
  Field,
  Pager,
  Search,
  Table,
  Toggle,
  useAsync,
  useToast,
  type Column,
} from "../ui";
import {
  PAGE_SIZE,
  BASE_PATH,
  errMessage,
  ErrorBanner,
  useDebounced,
  fmtDate,
  titleCase,
  ActiveBadge,
} from "./shared";
import type { RoleInfo, RoleListResponse, RoleRow } from "./roles";

export interface UserRow {
  id: string;
  email: string;
  full_name: string;
  role_id: string | null;
  role: RoleInfo | null;
  is_active: boolean;
  is_available: boolean;
  is_super_admin: boolean;
  /** True when the account's home organisation is not this one. */
  is_member: boolean;
  organization_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface UserListResponse {
  users: UserRow[];
  total: number;
  page: number;
  limit: number;
}

interface MyOrganization {
  organization_id: string;
  name: string;
  slug: string;
  role_id: string | null;
  role_name: string | null;
  is_default: boolean;
}

interface MyOrganizationsResponse {
  organizations: MyOrganization[];
}

// ============================================================ UsersView

interface UserForm {
  email: string;
  password: string;
  full_name: string;
  role_id: string;
  is_active: boolean;
  is_super_admin: boolean;
}

const EMPTY_USER_FORM: UserForm = {
  email: "",
  password: "",
  full_name: "",
  role_id: "",
  is_active: true,
  is_super_admin: false,
};

export function UsersView() {
  const { me, can } = useSession();
  const toast = useToast();
  const canWrite = can("users", "write");
  const canDelete = can("users", "delete");
  // `can()` only types read/write/delete; "assign" is a real action in the
  // catalogue, so the organisations check reads the permission map directly.
  const canAssignOrg = Boolean(me?.is_super_admin || me?.permissions?.organizations?.includes("assign"));

  const [search, setSearch] = useState("");
  const query = useDebounced(search);
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [query]);

  const { data, error, loading, reload } = useAsync<UserListResponse>(
    () => http.get<UserListResponse>(`/users${qs({ search: query, page, limit: PAGE_SIZE })}`),
    [query, page]
  );

  const users = data?.users ?? [];
  const total = data?.total ?? users.length;

  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [multiOrg, setMultiOrg] = useState(false);
  useEffect(() => {
    void http
      .get<RoleListResponse>(`/roles${qs({ limit: 100 })}`)
      .then((res) => setRoles(res.roles ?? []))
      .catch(() => setRoles([]));
    void http
      .get<MyOrganizationsResponse>("/me/organizations")
      .then((res) => setMultiOrg((res.organizations ?? []).length > 1))
      .catch(() => setMultiOrg(false));
  }, []);

  const [editing, setEditing] = useState<UserRow | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<UserForm>(EMPTY_USER_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [toRemove, setToRemove] = useState<UserRow | null>(null);

  // Member-role dialog: the only field of a borrowed account we own.
  const [memberTarget, setMemberTarget] = useState<UserRow | null>(null);
  const [memberRoleId, setMemberRoleId] = useState("");
  const [memberSaving, setMemberSaving] = useState(false);

  // Add-existing dialog.
  const [addOpen, setAddOpen] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addRoleId, setAddRoleId] = useState("");
  const [addSaving, setAddSaving] = useState(false);

  const defaultRoleId = useMemo(
    () => roles.find((r) => r.name === "agent" && r.is_system)?.id ?? "",
    [roles]
  );

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_USER_FORM, role_id: defaultRoleId });
    setDialogOpen(true);
  };

  const openEdit = (user: UserRow) => {
    setEditing(user);
    setForm({
      email: user.email,
      password: "",
      full_name: user.full_name,
      role_id: user.role_id ?? "",
      is_active: user.is_active,
      is_super_admin: user.is_super_admin,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.email.trim() || !form.full_name.trim()) {
      toast.push("Enter a name and an email address", "critical");
      return;
    }
    if (!editing && !form.password.trim()) {
      toast.push("Set a password for the new user", "critical");
      return;
    }
    if (!form.role_id) {
      toast.push("Pick a role", "critical");
      return;
    }

    setSubmitting(true);
    try {
      if (editing) {
        const body: Record<string, unknown> = {
          email: form.email.trim(),
          full_name: form.full_name.trim(),
          role_id: form.role_id,
          is_active: form.is_active,
        };
        if (form.password) body.password = form.password;
        if (me?.is_super_admin) body.is_super_admin = form.is_super_admin;
        await http.put(`/users/${editing.id}`, body);
        toast.push("User updated", "good");
      } else {
        await http.post("/users", {
          email: form.email.trim(),
          password: form.password,
          full_name: form.full_name.trim(),
          role_id: form.role_id,
          // Only a super admin may mint another one; otherwise the flag is left off.
          is_super_admin: me?.is_super_admin && form.is_super_admin ? true : undefined,
        });
        toast.push("User created", "good");
      }
      setDialogOpen(false);
      reload();
    } catch (e) {
      toast.push(errMessage(e, "Could not save the user"), "critical");
    } finally {
      setSubmitting(false);
    }
  };

  const saveMemberRole = async () => {
    if (!memberTarget || !memberRoleId) return;
    setMemberSaving(true);
    try {
      await http.put(`/users/${memberTarget.id}`, { role_id: memberRoleId });
      toast.push("Member role updated", "good");
      setMemberTarget(null);
      reload();
    } catch (e) {
      toast.push(errMessage(e, "Could not update the member's role"), "critical");
    } finally {
      setMemberSaving(false);
    }
  };

  const addExisting = async () => {
    if (!addEmail.trim()) {
      toast.push("Enter the email address of the existing account", "critical");
      return;
    }
    setAddSaving(true);
    try {
      await http.post("/organizations/members", {
        email: addEmail.trim(),
        role_id: addRoleId || undefined,
      });
      toast.push("Existing user added to this organisation", "good");
      setAddOpen(false);
      reload();
    } catch (e) {
      toast.push(errMessage(e, "Could not add that account"), "critical");
    } finally {
      setAddSaving(false);
    }
  };

  const remove = async () => {
    if (!toRemove) return;
    const wasMember = toRemove.is_member;
    try {
      await http.del(`/users/${toRemove.id}`);
      toast.push(wasMember ? "Membership removed" : "User deleted", "good");
      setToRemove(null);
      reload();
    } catch (e) {
      toast.push(errMessage(e, wasMember ? "Could not remove the member" : "Could not delete the user"), "critical");
      throw e; // keeps <Confirm> open so the operator can read the reason
    }
  };

  const copyInvite = async () => {
    const orgId = localStorage.getItem("selected_organization_id") || me?.organization_id || "";
    const url = `${window.location.origin}${BASE_PATH}/register?org=${orgId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.push("Invite link copied", "good");
    } catch {
      toast.push(url, "info");
    }
  };

  const roleName = (user: UserRow) => user.role?.name ?? "No role";

  const columns: Column<UserRow>[] = [
    {
      key: "full_name",
      header: "User",
      width: "300px",
      sortable: true,
      render: (u) => (
        <div>
          <div className="row">
            <span className="strong truncate">{u.full_name || u.email}</span>
            {u.id === me?.id && <Badge>You</Badge>}
            {u.is_super_admin && <Badge tone="brand">Super admin</Badge>}
            {u.is_member && <Badge tone="info">Member</Badge>}
          </div>
          <div className="muted truncate">{u.email}</div>
        </div>
      ),
    },
    {
      key: "role.name",
      header: "Role",
      sortable: true,
      render: (u) => <Badge>{titleCase(roleName(u))}</Badge>,
    },
    { key: "is_active", header: "Status", sortable: true, render: (u) => <ActiveBadge active={u.is_active} /> },
    { key: "created_at", header: "Created", sortable: true, render: (u) => fmtDate(u.created_at) },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (u) => (
        <div className="row" style={{ justifyContent: "flex-end" }}>
          {u.is_member ? (
            <>
              <button
                className="btn icon sm ghost"
                title="Change this member's role here"
                aria-label={`Change the role of ${u.full_name}`}
                disabled={!canWrite}
                onClick={() => {
                  setMemberTarget(u);
                  setMemberRoleId(u.role_id ?? "");
                }}
              >
                ✎
              </button>
              <button
                className="btn icon sm ghost"
                title={
                  u.id === me?.id
                    ? "You cannot remove yourself"
                    : "Detach this member from the organisation"
                }
                aria-label={`Remove ${u.full_name} from this organisation`}
                disabled={!canDelete || u.id === me?.id}
                onClick={() => setToRemove(u)}
              >
                ⇤
              </button>
            </>
          ) : (
            <>
              <button
                className="btn icon sm ghost"
                title="Edit this user"
                aria-label={`Edit ${u.full_name}`}
                disabled={!canWrite}
                onClick={() => openEdit(u)}
              >
                ✎
              </button>
              <button
                className="btn icon sm ghost"
                title={u.id === me?.id ? "You cannot delete yourself" : "Delete this user"}
                aria-label={`Delete ${u.full_name}`}
                disabled={!canDelete || u.id === me?.id}
                onClick={() => setToRemove(u)}
              >
                ✕
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  const editingSelf = editing?.id === me?.id;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Users</h1>
          <div className="sub">
            Everyone who can sign in to this organisation. <a href="/settings/roles">Manage roles</a>.
          </div>
        </div>
        <div className="page-actions">
          <button className="btn" onClick={() => void copyInvite()}>
            Copy invite link
          </button>
          {multiOrg && canAssignOrg && (
            <button
              className="btn"
              onClick={() => {
                setAddEmail("");
                setAddRoleId("");
                setAddOpen(true);
              }}
            >
              Add existing user
            </button>
          )}
          <button className="btn primary" disabled={!canWrite} onClick={openCreate}>
            + Add user
          </button>
        </div>
      </div>

      <ErrorBanner error={error} what="Could not load the users" />

      <div className="card flush">
        <div className="card-head">
          <h3>Your users</h3>
          <Search value={search} onChange={setSearch} placeholder="Search name or email…" />
        </div>
        <div className="card-body">
          {/* The native/member split changes what the row actions mean, so it is
              stated here in words rather than hidden behind the badge. */}
          <p className="muted" style={{ marginBottom: "var(--sp-3)" }}>
            A <strong className="strong">Member</strong> badge means the account lives in another
            organisation. You can change the role it holds here; removing it detaches the membership
            and leaves the account and its home organisation untouched. Everyone else is native to
            this organisation — editing and deleting apply to the account itself.
          </p>
          <Table
            columns={columns}
            rows={users}
            loading={loading}
            empty={
              <Empty
                title={query ? "No matching users" : "No users yet"}
                body={
                  query
                    ? `Nothing matches "${query}". Try a different name or email.`
                    : "Add the people who will answer conversations, or invite an existing account from another organisation."
                }
                icon="◍"
                action={
                  !query && canWrite ? (
                    <button className="btn primary sm" onClick={openCreate}>
                      + Add user
                    </button>
                  ) : undefined
                }
              />
            }
          />
        </div>
        <div className="card-foot">
          <Pager
            total={total}
            limit={PAGE_SIZE}
            offset={(page - 1) * PAGE_SIZE}
            itemName="users"
            onChange={(offset) => setPage(Math.floor(offset / PAGE_SIZE) + 1)}
          />
        </div>
      </div>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? "Edit user" : "Add user"}
      >
        <Field label="Full name" required>
          <input
            value={form.full_name}
            placeholder="Jordan Rivera"
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
          />
        </Field>
        <Field label="Email" required>
          <input
            type="email"
            value={form.email}
            placeholder="jordan@example.com"
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </Field>
        <Field
          label="Password"
          required={!editing}
          hint={editing ? "Leave blank to keep the existing password." : "At least 8 characters."}
        >
          <input
            type="password"
            value={form.password}
            autoComplete="new-password"
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
        </Field>
        <Field label="Role" required>
          <select value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value })}>
            <option value="">Select a role…</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {titleCase(r.name)}
                {r.is_system ? " (system)" : ""}
              </option>
            ))}
          </select>
        </Field>
        {editing && (
          <div className="setting">
            <div className="text">
              <div className="strong">Account active</div>
              <p>An inactive account cannot sign in.</p>
            </div>
            <Toggle
              checked={form.is_active}
              disabled={editingSelf}
              ariaLabel="Account active"
              onChange={(v) => setForm({ ...form, is_active: v })}
            />
          </div>
        )}
        {me?.is_super_admin && (
          <div className="setting">
            <div className="text">
              <div className="strong">Super admin</div>
              <p>Bypasses every permission check, in every organisation.</p>
            </div>
            <Toggle
              checked={form.is_super_admin}
              disabled={editingSelf && Boolean(editing?.is_super_admin)}
              ariaLabel="Super admin"
              onChange={(v) => setForm({ ...form, is_super_admin: v })}
            />
          </div>
        )}
        <DialogFooter>
          <button className="btn" onClick={() => setDialogOpen(false)} disabled={submitting}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => void save()} disabled={submitting}>
            {submitting && <span className="spinner" />}
            {editing ? "Update user" : "Create user"}
          </button>
        </DialogFooter>
      </Dialog>

      <Dialog
        open={memberTarget !== null}
        onClose={() => setMemberTarget(null)}
        title="Change member role"
      >
        {memberTarget && (
          <>
            <div className="card" style={{ marginBottom: "var(--sp-4)" }}>
              <div className="strong">{memberTarget.full_name || memberTarget.email}</div>
              <div className="muted">{memberTarget.email}</div>
            </div>
            <p className="muted" style={{ marginBottom: "var(--sp-4)" }}>
              This account belongs to another organisation. Only the role it holds here can be
              changed — its name, email and password are managed where the account lives.
            </p>
            <Field label="Role in this organisation" required>
              <select value={memberRoleId} onChange={(e) => setMemberRoleId(e.target.value)}>
                <option value="">Select a role…</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {titleCase(r.name)}
                    {r.is_system ? " (system)" : ""}
                  </option>
                ))}
              </select>
            </Field>
          </>
        )}
        <DialogFooter>
          <button className="btn" onClick={() => setMemberTarget(null)} disabled={memberSaving}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={memberSaving || !memberRoleId}
            onClick={() => void saveMemberRole()}
          >
            {memberSaving && <span className="spinner" />}
            Update role
          </button>
        </DialogFooter>
      </Dialog>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} title="Add existing user">
        <p className="muted" style={{ marginBottom: "var(--sp-4)" }}>
          Invites an account that already exists elsewhere. It joins as a member: its role here is
          yours to set, everything else stays with its home organisation.
        </p>
        <Field label="Email" required>
          <input
            type="email"
            value={addEmail}
            placeholder="colleague@example.com"
            onChange={(e) => setAddEmail(e.target.value)}
          />
        </Field>
        <Field label="Role" hint="Leave unset to use this organisation's default role.">
          <select value={addRoleId} onChange={(e) => setAddRoleId(e.target.value)}>
            <option value="">Default role</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {titleCase(r.name)}
              </option>
            ))}
          </select>
        </Field>
        <DialogFooter>
          <button className="btn" onClick={() => setAddOpen(false)} disabled={addSaving}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={addSaving || !addEmail.trim()}
            onClick={() => void addExisting()}
          >
            {addSaving && <span className="spinner" />}
            Add user
          </button>
        </DialogFooter>
      </Dialog>

      <Confirm
        open={toRemove !== null}
        onClose={() => setToRemove(null)}
        onConfirm={remove}
        danger
        title={toRemove?.is_member ? "Remove member" : "Delete user"}
        confirmLabel={toRemove?.is_member ? "Remove member" : "Delete user"}
        body={
          toRemove?.is_member ? (
            <>
              <strong className="strong">{toRemove.full_name || toRemove.email}</strong> is detached
              from this organisation. The account itself, and the organisation it belongs to, are
              left untouched — you can invite it back at any time.
            </>
          ) : (
            <>
              <strong className="strong">{toRemove?.full_name || toRemove?.email}</strong> and every
              organisation membership it holds are deleted. This cannot be undone.
            </>
          )
        }
      />
    </div>
  );
}
